import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

const lines = ["Every ad channel.", "One AI brain."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.15 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 overflow-hidden" data-testid="hero-section">
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]"
        style={{ background: "radial-gradient(circle, #FF4D00 0%, transparent 65%)" }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2"
          data-testid="hero-overline"
        >
          <Sparkles size={13} className="text-[#FF4D00]" /> The 5ads.ai platform
        </motion.p>

        <h1
          className="font-heading font-semibold tracking-tighter text-neutral-900 text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1.02]"
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {i === 1 ? (
                  <>
                    One <span className="text-[#FF4D00]">AI brain</span>.
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 md:flex md:items-end md:justify-between gap-12">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="text-base md:text-lg leading-relaxed text-neutral-600 max-w-xl"
            data-testid="hero-subtext"
          >
            5ads.ai is a family of AI-powered ad managers. Facebook &amp; Instagram today,
            Google Search tomorrow — each app runs your campaigns with a friendly AI team,
            and you approve every move.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="flex items-center gap-4 mt-8 md:mt-0 shrink-0"
          >
            <a
              href="https://social.5ads.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="group bg-neutral-900 text-white text-sm font-medium px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-[#FF4D00] transition-colors duration-300"
            >
              Explore social.5ads.ai
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
            </a>
            <a
              href="#apps"
              data-testid="hero-secondary-cta"
              className="text-sm font-medium text-neutral-700 border border-neutral-300 px-6 py-3.5 rounded-full hover:border-neutral-900 transition-colors duration-200"
            >
              See the apps
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 md:mt-20"
        >
          <motion.div
            style={{ y: imgY, scale: imgScale }}
            className="rounded-2xl overflow-hidden border border-neutral-200 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
          >
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
              alt="Ad performance analytics dashboard"
              className="w-full h-[340px] md:h-[520px] object-cover"
              data-testid="hero-dashboard-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.5 }}
            className="absolute -bottom-6 left-6 md:left-12 backdrop-blur-xl bg-white/85 border border-neutral-200 rounded-2xl px-5 py-4 shadow-[0_8px_30px_rgb(0,0,0,0.08)]"
            data-testid="hero-floating-card"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">AI decision</p>
            <p className="text-sm font-medium text-neutral-900 mt-1">
              Scale budget +20% — CPL down <span className="text-[#FF4D00]">31%</span>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
